export type RegionalService = {
  slug: string;
  name: string;
  family: "reinigung" | "rueckbau";
  intro: string;
  terms: string[];
  sections: { title: string; text: string }[];
  preparation: string[];
  costQuestion: string;
  costAnswer: string;
  faq: { question: string; answer: string };
};

// One destination per service intent. Company synonyms, price queries and
// spelling variants belong to that destination rather than additional pages.
export const regionalServices: RegionalService[] = [
  {
    slug: "reinigungsfirmen",
    name: "Reinigungsfirmen",
    family: "reinigung",
    intro:
      "Sie suchen eine Reinigungsfirma in der Nähe? Vergleichen Sie Reinigungsunternehmen nach Einsatzort, Leistungen und veröffentlichten Kontaktdaten. Ob Putzfirma, Putzinstitut oder Gebäudedienstleister: Entscheidend ist, ob der Betrieb Ihre konkrete Reinigung übernimmt.",
    terms: ["reinig", "putz", "hauswart", "nettoy", "pulizi", "cleaning"],
    sections: [
      {
        title: "Die passende Reinigung auswählen",
        text: "Nennen Sie zunächst den Anlass: regelmässige Pflege, Wohnungsabgabe, Bauabschluss oder einzelne verschmutzte Flächen. Beschreiben Sie Räume, Oberflächen und Häufigkeit. So kann die Reinigungsfirma prüfen, welche Mitarbeitenden und Geräte für Ihren Auftrag nötig sind.",
      },
      {
        title: "Putzfirma und Reinigungsunternehmen vergleichen",
        text: "Prüfen Sie Referenzen für ähnliche Objekte und vergleichen Sie ein schriftliches Leistungsverzeichnis. Vereinbaren Sie Schlüsselverwaltung, Kontaktperson und den Umgang mit Zusatzarbeiten. Der Firmenstandort allein sagt noch nicht, ob Ihr Objekt im Einsatzgebiet liegt.",
      },
    ],
    preparation: [
      "Gemeinde, Postleitzahl und Objektart nennen.",
      "Räume, Flächen und gewünschte Häufigkeit beschreiben.",
      "Fotos, Zugang und gewünschtes Zeitfenster ergänzen.",
    ],
    costQuestion: "Was kostet es, eine Reinigungsfirma zu beauftragen?",
    costAnswer:
      "Die Kosten richten sich nach Leistung, Fläche, Zustand und Häufigkeit. Lassen Sie Arbeitszeit oder Pauschale, Material, Anfahrt und Mehrwertsteuer ausweisen. Vergleichen Sie mehrere Offerten mit demselben Umfang und klären Sie, ob ein Mindestaufwand berechnet wird.",
    faq: {
      question: "Wie finde ich eine Reinigungsfirma in der Nähe?",
      answer:
        "Vergleichen Sie die veröffentlichten Standort- und Leistungsangaben. Bestätigen Sie mit dem Anbieter, ob er Ihre Gemeinde bedient, wann er verfügbar ist und ob Anfahrtskosten anfallen. Sie können die veröffentlichten Kontaktdaten direkt nutzen.",
    },
  },
  {
    slug: "baureinigung",
    name: "Baureinigung",
    family: "reinigung",
    intro:
      "Für die Reinigung am Bau zählt der richtige Zeitpunkt. Beschreiben Sie Neubau, Umbau oder Renovation und klären Sie mit einer Reinigungsfirma für Baustellen, ob laufende Baureinigung, Bauendreinigung oder eine Grundreinigung nach Bauarbeiten benötigt wird.",
    terms: [
      "baureinig",
      "bauendreinig",
      "bauschlussreinig",
      "baustellenreinig",
      "bau reinig",
      "nettoyage de chantier",
    ],
    sections: [
      {
        title: "Baureinigung während der Arbeiten",
        text: "Stimmen Sie die Einsätze mit dem Bauablauf ab. Halten Sie fest, welche Bereiche zugänglich sind und ob Grobschmutz, Verpackungen und Abfälle dazugehören. Vereinbaren Sie, wer vor einem Einsatz die Flächen freigibt und wie weitere Handwerksarbeiten berücksichtigt werden.",
      },
      {
        title: "Bauendreinigung und Grundreinigung nach Bauarbeiten",
        text: "Für die abschliessende Reinigung erfassen Sie Böden, Fenster, Sanitäranlagen und Einbauten. Beschreiben Sie Baustaub und sichtbare Rückstände und lassen Sie empfindliche oder frisch behandelte Materialien vorab beurteilen. Der vereinbarte Übergabezustand sollte im Angebot stehen.",
      },
    ],
    preparation: [
      "Bauzustand, Flächen und betroffene Materialien nennen.",
      "Reinigungsetappen mit Bauleitung und Gewerken abstimmen.",
      "Fenster, Storen, Geräte und Restabfälle erfassen.",
    ],
    costQuestion: "Baureinigung und Bauendreinigung: Welche Preise sind zu vergleichen?",
    costAnswer:
      "Die Kosten einer Bauendreinigung hängen von Fläche, Rückständen, Oberflächen und Erreichbarkeit ab. Für vergleichbare Preise für die Bauendreinigung benötigen die Firmen dieselben Angaben. Lassen Sie zusätzliche Etappen, Material, Anfahrt, Abfallentsorgung und Mehrwertsteuer ausweisen und klären Sie die Abrechnung nach Fläche, Stunden oder Pauschale.",
    faq: {
      question: "Wann soll die Bauendreinigung stattfinden?",
      answer:
        "Vereinbaren Sie den Termin anhand des tatsächlichen Baufortschritts und der geplanten Übergabe. Klären Sie, welche Arbeiten vorher abgeschlossen sein müssen und wie eine erneute Reinigung nach späteren Handwerkereinsätzen berechnet wird.",
    },
  },
  {
    slug: "rueckbau",
    name: "Rückbau",
    family: "rueckbau",
    intro:
      "Sie suchen ein Rückbauunternehmen oder eine Rückbaufirma für einen Umbau? Beschreiben Sie für Abbruch und Rückbau, welche Bauteile entfernt werden und welche Bereiche erhalten bleiben sollen. Vergleichen Sie Erfahrung, Leistungsumfang und Entsorgungswege.",
    terms: ["rückbau", "rueckbau", "abbruch", "abriss", "demol"],
    sections: [
      {
        title: "Abbruch und Rückbauarbeiten abgrenzen",
        text: "Einzelne Innenausbauten, Gebäudeteile und ein vollständiger Hausrückbau sind unterschiedliche Aufträge. Lassen Sie den Bestand besichtigen und die zu entfernenden Bauteile eindeutig erfassen. Vereinbaren Sie Schutzmassnahmen und den Zustand, in dem das Objekt übergeben wird.",
      },
      {
        title: "Abriss und Rückbau organisieren",
        text: "Klären Sie Zuständigkeiten für Vorabklärungen, Baustelleneinrichtung und Abstimmung mit weiteren Fachbetrieben. Für die Offerte zählen auch Zufahrt, Transportwege, Platz für Container und zu erhaltende Einrichtungen. Fragen Sie nach Referenzen für vergleichbare Arbeiten.",
      },
    ],
    preparation: [
      "Pläne und Fotos des heutigen Bestands bereitstellen.",
      "Zu entfernende und zu erhaltende Bauteile markieren lassen.",
      "Zufahrt, Transportwege und Übergabetermin besprechen.",
    ],
    costQuestion: "Wovon hängen die Kosten für Abbruch und Rückbau ab?",
    costAnswer:
      "Umfang, Bauweise, Zugänglichkeit und Materialien beeinflussen den Aufwand. Vergleichen Sie Baustelleneinrichtung, Arbeitszeit, Schutzmassnahmen, Abtransport und Entsorgung bei gleichem Umfang. Vereinbaren Sie nach einer Besichtigung, wie Zusatzarbeiten und unerwartete Funde behandelt werden.",
    faq: {
      question: "Übernimmt jede Räumungsfirma auch Rückbau?",
      answer:
        "Eine Räumung beweglicher Gegenstände belegt keine Erfahrung mit baulichen Eingriffen. Prüfen Sie die ausdrücklich veröffentlichten Leistungen und lassen Sie sich bestätigen, dass der Betrieb die vorgesehenen Abbruch- und Rückbauarbeiten ausführt.",
    },
  },
  {
    slug: "entkernung",
    name: "Entkernung",
    family: "rueckbau",
    intro:
      "Für die Entkernung von Gebäuden oder Wohnungen braucht es eine klare Bestandsaufnahme. Vergleichen Sie Entkernungsfirmen anhand ähnlicher Projekte und vereinbaren Sie, welche Innenausbauten entfernt und welche Gebäudeteile erhalten werden.",
    terms: ["entkern"],
    sections: [
      {
        title: "Entkernung im Altbau und in der Wohnung",
        text: "Für die Entkernung eines Altbaus sind Pläne, frühere Umbauten und Angaben zu Materialien hilfreich. Bei einer Wohnung erfassen Sie zusätzlich gemeinsame Zugänge und weiter genutzte Räume. Lassen Sie den Bestand und notwendige Untersuchungen durch Fachleute beurteilen und die Koordination mit Verwaltung und Planung festhalten.",
      },
      {
        title: "Entrümpelung, Entkernung und Entsorgung",
        text: "Erfassen Sie bewegliche Gegenstände und feste Einbauten getrennt. Bei Abbruch und Entkernung gehört in die Offerte, was entfernt, wiederverwendet oder entsorgt wird. Vereinbaren Sie für die Gebäudeentkernung Container, Transportwege, Materialtrennung und die Dokumentation der Entsorgung.",
      },
    ],
    preparation: [
      "Räume, Einbauten und zu erhaltende Bereiche dokumentieren.",
      "Pläne, Baualter und bekannte frühere Umbauten nennen.",
      "Zugang und Abstimmung mit den folgenden Gewerken klären.",
    ],
    costQuestion: "Was bestimmt die Kosten einer Gebäudeentkernung?",
    costAnswer:
      "Flächen, Ausbauzustand, Materialien und Transportwege bestimmen den Aufwand. Vergleichen Sie Demontage, Schutzmassnahmen, Container, Abtransport und Entsorgung im gleichen Umfang. Lassen Sie notwendige Vorabklärungen und die Abrechnung zusätzlicher Arbeiten schriftlich festhalten.",
    faq: {
      question: "Wie wähle ich eine Firma für Entkernung aus?",
      answer:
        "Fragen Sie nach Referenzen für eine ähnliche Entkernung im Altbau, eine Wohnungsentkernung oder ein ganzes Gebäude. Ob Sie Abriss und Entkernung gemeinsam oder einzelne Innenbereiche planen: Klären Sie Leistungsumfang, Ansprechperson, Zuständigkeiten und Übergabezustand direkt mit der Entkernungsfirma.",
    },
  },
  {
    slug: "fensterreinigung",
    name: "Fensterreinigung",
    family: "reinigung",
    intro:
      "Vergleichen Sie Firmen für die Reinigung Ihrer Fenster und Glasflächen. Anzahl, Grösse und Erreichbarkeit der Fenster helfen dem Betrieb, den Einsatz vorzubereiten. Legen Sie fest, ob Rahmen, Falze und Storen dazugehören.",
    terms: ["fensterreinig", "glasreinig", "fenster reinig", "vitres"],
    sections: [
      {
        title: "Glas, Rahmen und Storen erfassen",
        text: "Erstellen Sie eine Übersicht der Fenster, Balkontüren und weiteren Glasflächen. Beschreiben Sie Verschmutzungen und gewünschte Zusatzleistungen. Halten Sie fest, ob beide Glasseiten, Rahmen, Falze und Storen gereinigt werden sollen und welche Teile empfindlich sind.",
      },
      {
        title: "Zugang zur Fensterreinigung planen",
        text: "Nennen Sie Geschoss, Öffnungsmöglichkeiten und schwer erreichbare Flächen. Der Betrieb sollte den Zugang beurteilen und benötigte Hilfsmittel im Angebot aufführen. Stimmen Sie Innenzugang und mögliche Aussenarbeiten mit den Nutzern des Gebäudes ab.",
      },
    ],
    preparation: [
      "Fenster nach Anzahl und ungefährer Grösse erfassen.",
      "Geschoss und Zugang mit Fotos beschreiben.",
      "Rahmen, Falze, Storen und Glasdächer separat nennen.",
    ],
    costQuestion: "Was beeinflusst die Kosten einer Fensterreinigung?",
    costAnswer:
      "Anzahl, Fläche, Verschmutzung und Zugänglichkeit bestimmen den Aufwand. Fragen Sie, ob Rahmen und Storen enthalten sind und ob Anfahrt, Hebebühne oder weitere Hilfsmittel zusätzlich berechnet werden. Vergleichen Sie Offerten für dieselben Glasflächen und Leistungen.",
    faq: {
      question: "Sind Storen bei der Fensterreinigung enthalten?",
      answer:
        "Das hängt vom vereinbarten Umfang ab. Lassen Sie Storen, Rahmen und Falze ausdrücklich in der Offerte aufführen. Nennen Sie Material und Zustand, damit der Betrieb die passende Reinigung beurteilen kann.",
    },
  },
  {
    slug: "hauswartung",
    name: "Hauswartung",
    family: "reinigung",
    intro:
      "Vergleichen Sie Firmen für Hauswartung anhand eines klaren Pflichtenhefts. Für Mietliegenschaften und Stockwerkeigentum zählen regelmässige Betreuung, dokumentierte Aufgaben und eine erreichbare Ansprechperson.",
    terms: ["hauswart", "hausmeister", "conciergerie"],
    sections: [
      {
        title: "Aufgaben der Hauswartungsfirma festlegen",
        text: "Beschreiben Sie gemeinsame Innen- und Aussenflächen, Kontrollgänge und gewünschte Reinigungsarbeiten. Vereinbaren Sie, welche Aufgaben regelmässig erfolgen und welche separat beauftragt werden. Grenzfälle wie kleinere Reparaturen sollten im Pflichtenheft geklärt sein.",
      },
      {
        title: "Betreuung und Vertretung vereinbaren",
        text: "Fragen Sie nach Einsatzrhythmus, Schlüsselverwaltung und Vertretung bei Ausfällen. Halten Sie fest, wer Meldungen aus der Liegenschaft entgegennimmt und wer Zusatzarbeiten freigibt. Eine einfache Leistungsdokumentation erleichtert den Austausch mit der Verwaltung.",
      },
    ],
    preparation: [
      "Anzahl Gebäude, Wohnungen und gemeinsame Flächen nennen.",
      "Bestehendes Pflichtenheft und gewünschte Intervalle bereitstellen.",
      "Zuständigkeiten von Verwaltung und Eigentümerschaft klären.",
    ],
    costQuestion: "Wie lassen sich die Kosten für Hauswartung vergleichen?",
    costAnswer:
      "Vergleichen Sie Pauschalen anhand desselben Pflichtenhefts und Einsatzrhythmus. Lassen Sie Reinigungsmaterial, Anfahrt, Vertretung und zusätzliche Einsätze abgrenzen. Prüfen Sie, welche Arbeiten nach Stunden abgerechnet werden und wer diese freigeben darf.",
    faq: {
      question: "Gehört Treppenhausreinigung zur Hauswartung?",
      answer:
        "Sie kann Teil des Auftrags sein, ist aber ausdrücklich zu vereinbaren. Legen Sie Häufigkeit, zu reinigende Flächen und zusätzliche Arbeiten wie Fenster oder Handläufe im Pflichtenheft fest.",
    },
  },
  {
    slug: "gebaeudereinigung",
    name: "Gebäudereinigung",
    family: "reinigung",
    intro:
      "Für die Gebäudereinigung in Ihrer Nähe vergleichen Sie Betriebe nach Objektart und benötigten Leistungen. Ein Leistungsverzeichnis macht sichtbar, welche Räume und Flächen in welchen Intervallen gereinigt werden.",
    terms: ["gebäudereinig", "gebaeudereinig", "liegenschaftsreinig"],
    sections: [
      {
        title: "Das Gebäude nach Nutzungsbereichen erfassen",
        text: "Erfassen Sie Eingänge, Verkehrsflächen, Sanitärbereiche und weitere gemeinsam genutzte Räume. Beschreiben Sie Beläge und Nutzungszeiten. Klären Sie, ob Fensterreinigung, Grundreinigung und Aussenflächen Teil des Auftrags sind oder eigene Angebote benötigen.",
      },
      {
        title: "Reinigungsplan und Qualität abstimmen",
        text: "Vereinbaren Sie Aufgaben, Intervalle und eine Ansprechperson für Rückmeldungen. Besprechen Sie Zugang und Schlüsselverwaltung sowie die Reinigung während des laufenden Betriebs. Halten Sie fest, wie Zusatzleistungen und Änderungen des Flächenumfangs dokumentiert werden.",
      },
    ],
    preparation: [
      "Flächenübersicht und Nutzung des Gebäudes bereitstellen.",
      "Reinigungsintervalle je Raumgruppe festlegen.",
      "Zugang, Schlüssel und Zeiten mit den Nutzern abstimmen.",
    ],
    costQuestion: "Wovon hängen die Preise für Gebäudereinigung ab?",
    costAnswer:
      "Fläche, Nutzung, Beläge und Intervalle beeinflussen den Aufwand. Stellen Sie Angebote mit gleichem Reinigungsplan gegenüber und prüfen Sie Material, Verbrauchsartikel, Anfahrt und Mehrwertsteuer. Eine Pauschale sollte den eingeschlossenen Umfang genau benennen.",
    faq: {
      question: "Führt jede Gebäudereinigungsfirma auch Spezialreinigungen aus?",
      answer:
        "Prüfen Sie die veröffentlichten Leistungen und fragen Sie gezielt nach dem Auftrag. Fassaden, schwer erreichbare Glasflächen oder besondere Oberflächen können zusätzliche Ausrüstung und ein separates Angebot erfordern.",
    },
  },
  {
    slug: "wohnungsreinigung",
    name: "Wohnungsreinigung",
    family: "reinigung",
    intro:
      "Sie möchten Ihre Wohnung reinigen lassen? Beschreiben Sie, ob Sie eine einmalige Wohnungsreinigung oder regelmässige Haushaltsreinigung suchen. Vergleichen Sie Putzfirmen nach Umfang, Zeitfenster und dem Zugang zu Ihrer Wohnung.",
    terms: ["wohnungsreinig", "haushaltsreinig", "haushalt", "privatreinig", "putzhilfe"],
    sections: [
      {
        title: "Haushaltsreinigung nach Ihren Bedürfnissen",
        text: "Legen Sie fest, welche Zimmer, Oberflächen und Geräte gereinigt werden sollen. Für wiederkehrende Einsätze hilft eine Prioritätenliste. Klären Sie, ob Verbrauchsmaterial bereitgestellt wird und wie empfindliche Böden, Möbel oder Gegenstände berücksichtigt werden.",
      },
      {
        title: "Einmaliger Auftrag oder regelmässiger Einsatz",
        text: "Nennen Sie gewünschte Häufigkeit und verfügbare Zeitfenster. Vereinbaren Sie Zugang, Schlüsselübergabe und den Umgang mit Terminänderungen. Wenn Sie ausziehen und eine Abnahmegarantie benötigen, beschreiben Sie ausdrücklich eine Umzugs- oder Endreinigung.",
      },
    ],
    preparation: [
      "Wohnfläche, Zimmer und gewünschte Arbeiten nennen.",
      "Besondere Oberflächen und Geräte beschreiben.",
      "Häufigkeit, Zugang und Materialbereitstellung klären.",
    ],
    costQuestion: "Was kostet es, eine Wohnung reinigen zu lassen?",
    costAnswer:
      "Kosten hängen von Grösse, Zustand, Aufgaben und Häufigkeit ab. Vergleichen Sie Stundenansätze oder Pauschalen mit identischen Leistungen. Fragen Sie nach Mindestdauer, Material, Anfahrt und Mehrwertsteuer sowie den Konditionen bei Terminänderungen.",
    faq: {
      question: "Ist Haushaltsreinigung eine Endreinigung mit Abnahmegarantie?",
      answer:
        "Eine gewöhnliche Haushaltsreinigung umfasst die vereinbarten Pflegearbeiten. Für die Wohnungsabgabe benötigen Sie einen ausdrücklich vereinbarten Umfang und gegebenenfalls eine schriftliche Abnahmegarantie mit klaren Bedingungen.",
    },
  },
  {
    slug: "umzugsreinigung",
    name: "Umzugsreinigung",
    family: "reinigung",
    intro:
      "Für die Endreinigung Ihrer Wohnung zählt eine klare Vereinbarung vor der Übergabe. Vergleichen Sie Firmen für Umzugsreinigung und Wohnungsreinigung mit Abnahmegarantie nach Leistungsumfang, Termin und Bedingungen der Nachreinigung.",
    terms: ["umzugsreinig", "endreinig", "abnahmegarantie", "abgabereinig"],
    sections: [
      {
        title: "Endreinigung der Wohnung vorbereiten",
        text: "Erfassen Sie Wohnfläche, Zimmer, Fenster, Storen und Nebenräume. Nennen Sie Backofen, Kühlschrank und weitere Geräte. Klären Sie, wann die Wohnung leer und für die Reinigung zugänglich ist und ob Keller, Balkon oder Garage zum Auftrag gehören.",
      },
      {
        title: "Wohnungsreinigung mit Abnahmegarantie",
        text: "Lassen Sie die Abnahmegarantie schriftlich erklären. Vereinbaren Sie die Teilnahme an der Übergabe, den Umfang einer Nachreinigung und die zuständige Kontaktperson. Fragen Sie, wie Beanstandungen gemeldet werden und welche Leistungen ausgeschlossen sind.",
      },
    ],
    preparation: [
      "Wohnfläche, Zimmerzahl und Geräte erfassen.",
      "Auszug und Übergabetermin mit Verwaltung abstimmen.",
      "Fenster, Storen, Balkon und Nebenräume ausdrücklich nennen.",
    ],
    costQuestion: "Was kostet die Endreinigung einer Wohnung?",
    costAnswer:
      "Die Kosten einer Endreinigung richten sich nach Fläche, Verschmutzung und Zusatzleistungen. Vergleichen Sie Angebote inklusive Fenster, Storen und Geräten sowie Anfahrt und Mehrwertsteuer. Prüfen Sie, ob Abnahmegarantie, Übergabeteilnahme und Nachreinigung enthalten sind.",
    faq: {
      question: "Was bedeutet Abnahmegarantie bei einer Umzugsreinigung?",
      answer:
        "Die konkreten Bedingungen ergeben sich aus Ihrem Vertrag mit dem Betrieb. Halten Sie fest, welche Beanstandungen eine Nachreinigung auslösen, wann sie erfolgt und ob die Firma bei der Wohnungsabgabe anwesend ist.",
    },
  },
  {
    slug: "unterhaltsreinigung",
    name: "Unterhaltsreinigung",
    family: "reinigung",
    intro:
      "Bei der Unterhaltsreinigung werden vereinbarte Arbeiten regelmässig ausgeführt. Vergleichen Sie Firmen anhand eines Reinigungsplans für Ihre Wohnung, Geschäftsräume oder Liegenschaft und stimmen Sie Intervalle auf die Nutzung ab.",
    terms: ["unterhaltsreinig", "regelmässige reinig", "regelmaessige reinig"],
    sections: [
      {
        title: "Einen verbindlichen Reinigungsplan erstellen",
        text: "Ordnen Sie Aufgaben einzelnen Bereichen zu: Böden, Oberflächen, Sanitäranlagen und weitere Nutzflächen. Legen Sie Häufigkeit und Zeitfenster fest. Seltenere Arbeiten wie Fensterreinigung oder intensive Grundreinigung sollten separat beschrieben werden.",
      },
      {
        title: "Leistung im laufenden Betrieb abstimmen",
        text: "Vereinbaren Sie Zugang, Schlüsselverwaltung und eine Vertretung bei Ausfällen. Eine gemeinsame Begehung hilft, den tatsächlichen Aufwand einzuschätzen. Halten Sie fest, wie Rückmeldungen, Zusatzaufgaben und Änderungen am Reinigungsplan behandelt werden.",
      },
    ],
    preparation: [
      "Flächen und Aufgaben nach Bereichen auflisten.",
      "Gewünschte Intervalle und Nutzungszeiten nennen.",
      "Material, Schlüssel und Ansprechperson vereinbaren.",
    ],
    costQuestion: "Wie wird Unterhaltsreinigung abgerechnet?",
    costAnswer:
      "Je nach Vereinbarung wird nach Aufwand oder mit einer Pauschale abgerechnet. Vergleichen Sie den Preis stets zusammen mit dem Reinigungsplan. Klären Sie Verbrauchsmaterial, Mindestaufwand, Anfahrt und die Kosten zusätzlicher Einsätze.",
    faq: {
      question: "Ist eine Grundreinigung im Unterhaltsvertrag enthalten?",
      answer:
        "Das hängt vom Leistungsverzeichnis ab. Wiederkehrende Pflege und eine intensive Grundreinigung sollten mit Häufigkeit und Umfang getrennt aufgeführt sein, damit die Abrechnung nachvollziehbar bleibt.",
    },
  },
  {
    slug: "treppenhausreinigung",
    name: "Treppenhausreinigung",
    family: "reinigung",
    intro:
      "Für die Treppenhausreinigung in Mehrfamilienhäusern und Geschäftsliegenschaften braucht es klare Zuständigkeiten. Vergleichen Sie Firmen für Treppen, Podeste, Eingangsbereiche und weitere gemeinsam genutzte Flächen.",
    terms: ["treppenhausreinig", "treppenreinig", "stiegenhaus"],
    sections: [
      {
        title: "Gemeinsame Flächen vollständig erfassen",
        text: "Nennen Sie Geschosse, Treppenbeläge, Handläufe und Eingangsflächen. Briefkästen, Lift, Kellerzugang und Fenster können zusätzliche Positionen sein. Legen Sie fest, ob nur Böden oder auch Kontaktflächen und weitere Einrichtungen gereinigt werden.",
      },
      {
        title: "Rhythmus und Zugang vereinbaren",
        text: "Stimmen Sie Einsatztage und Zugang mit Verwaltung oder Eigentümerschaft ab. Halten Sie fest, wer Rückmeldungen entgegennimmt. Zusätzliche Einsätze nach Umzügen oder Bauarbeiten sollten gesondert beauftragt und abgerechnet werden.",
      },
    ],
    preparation: [
      "Geschosse, Zugänge und Beläge dokumentieren.",
      "Lift, Fenster und Nebenflächen separat aufführen.",
      "Reinigungsrhythmus und Zuständigkeiten festlegen.",
    ],
    costQuestion: "Was bestimmt die Kosten einer Treppenhausreinigung?",
    costAnswer:
      "Anzahl Geschosse, Fläche, Beläge und Einsatzhäufigkeit beeinflussen den Aufwand. Vergleichen Sie Pauschalen mit denselben Aufgaben und Intervallen. Prüfen Sie, ob Fenster, Lift, Reinigungsmittel, Anfahrt und Mehrwertsteuer enthalten sind.",
    faq: {
      question: "Kann Treppenhausreinigung mit Hauswartung kombiniert werden?",
      answer:
        "Einige Betriebe bieten beides an. Lassen Sie die Reinigungsarbeiten und die übrigen Hauswartungsaufgaben in einem Pflichtenheft voneinander abgrenzen und klären Sie die Verantwortung für Zusatzaufträge.",
    },
  },
  {
    slug: "grundreinigung",
    name: "Grundreinigung",
    family: "reinigung",
    intro:
      "Eine Grundreinigung richtet sich nach Zustand und Material der betroffenen Flächen. Beschreiben Sie für Ihre Wohnung oder Liegenschaft, welche Ablagerungen und Verschmutzungen über die regelmässige Pflege hinaus entfernt werden sollen.",
    terms: ["grundreinig", "tiefenreinig", "intensivreinig"],
    sections: [
      {
        title: "Oberflächen und Verschmutzungen beschreiben",
        text: "Nennen Sie Böden, Fugen, Sanitäranlagen und weitere betroffene Flächen. Fotos von Kalk, Fett oder anderen Rückständen helfen bei der Einschätzung. Lassen Sie empfindliche Materialien vor Ort beurteilen und den vereinbarten Umfang festhalten.",
      },
      {
        title: "Grundreinigung der Wohnung vorbereiten",
        text: "Klären Sie, welche Räume und Möbel zugänglich sein müssen und wer Gegenstände wegräumt. Vereinbaren Sie, wann die Flächen anschliessend wieder genutzt werden können. Nach Bauarbeiten sollte der Betrieb zusätzlich den Bauzustand und verbliebene Rückstände kennen.",
      },
    ],
    preparation: [
      "Betroffene Räume, Flächen und Materialien nennen.",
      "Verschmutzungen mit Fotos dokumentieren.",
      "Zugang, Möbel und anschliessende Nutzung abstimmen.",
    ],
    costQuestion: "Was kostet eine Grundreinigung der Wohnung?",
    costAnswer:
      "Die Kosten richten sich nach Fläche, Verschmutzung, Material und Reinigungstiefe. Lassen Sie die einzubeziehenden Oberflächen einzeln beschreiben. Prüfen Sie Material, Geräteeinsatz, Anfahrt und Mehrwertsteuer und klären Sie eine Pauschale oder Abrechnung nach Stunden.",
    faq: {
      question: "Ist Grundreinigung nach Bauarbeiten dieselbe Leistung?",
      answer:
        "Baustaub und Rückstände nach Bauarbeiten erfordern einen entsprechend beschriebenen Auftrag. Geben Sie den Bauzustand an und prüfen Sie auch die Leistungsangaben zur Bauendreinigung, damit Flächen und Übergabezustand klar vereinbart sind.",
    },
  },
  {
    slug: "fassadenreinigung",
    name: "Fassadenreinigung",
    family: "reinigung",
    intro:
      "Sie möchten Ihre Hausfassade reinigen lassen? Beschreiben Sie Material, Fläche und sichtbare Verschmutzung. Vergleichen Sie Firmen, die Erfahrung mit vergleichbaren Fassaden und dem Zugang an Ihrem Objekt angeben.",
    terms: ["fassadenreinig", "fassade reinig", "hausfassade", "facadenreinig"],
    sections: [
      {
        title: "Fassade vor Ort beurteilen lassen",
        text: "Nennen Sie bekannte Beschichtungen, Schäden und empfindliche Bauteile. Der Betrieb sollte Material und Zustand beurteilen, bevor ein Verfahren vereinbart wird. Halten Sie fest, welche Fassadenseiten, Sockel und weiteren Flächen zur Reinigung gehören.",
      },
      {
        title: "Zugang und Schutz angrenzender Flächen",
        text: "Beschreiben Sie Gebäudehöhe, Zufahrt und Platzverhältnisse. Klären Sie, ob Gerüst oder Hebebühne benötigt werden. Fragen Sie nach dem Schutz von Fenstern, Umgebung und angrenzenden Flächen sowie dem Umgang mit anfallendem Reinigungswasser.",
      },
    ],
    preparation: [
      "Fassadenmaterial und ungefähre Fläche nennen.",
      "Verschmutzungen, Schäden und Umgebung fotografieren.",
      "Gebäudehöhe, Zufahrt und Platzverhältnisse beschreiben.",
    ],
    costQuestion: "Wovon hängen die Kosten einer Fassadenreinigung ab?",
    costAnswer:
      "Material, Fläche, Verschmutzung und Erreichbarkeit bestimmen den Aufwand. Vergleichen Sie nach einer Besichtigung Offerten inklusive Gerüst oder Hebebühne, Schutzmassnahmen und Umgang mit Reinigungswasser. Lassen Sie Zusatzarbeiten und Mehrwertsteuer gesondert ausweisen.",
    faq: {
      question: "Kann jede Hausfassade gleich gereinigt werden?",
      answer:
        "Lassen Sie Material, Beschichtung und Zustand durch den Betrieb beurteilen. Vereinbaren Sie das vorgeschlagene Vorgehen für Ihr konkretes Objekt und klären Sie vorab, ob eine Musterfläche sinnvoll ist.",
    },
  },
  {
    slug: "bueroreinigung",
    name: "Büroreinigung",
    family: "reinigung",
    intro:
      "Vergleichen Sie Firmen für die Reinigung Ihrer Büroräume anhand von Fläche, Nutzung und Einsatzzeiten. Ein klarer Reinigungsplan verbindet Arbeitsplätze, Sitzungsräume, Küche und Sanitärbereiche zu einem nachvollziehbaren Auftrag.",
    terms: ["büroreinig", "bueroreinig", "geschäftsreinig", "geschaeftsreinig"],
    sections: [
      {
        title: "Aufgaben je Bürobereich festlegen",
        text: "Unterscheiden Sie freie Oberflächen, Böden, Gemeinschaftsküche und Sanitäranlagen. Vereinbaren Sie den Umgang mit persönlichen Gegenständen und technischen Geräten. Fenster, Verbrauchsartikel und zusätzliche Grundreinigungen sollten ausdrücklich im Leistungsverzeichnis stehen.",
      },
      {
        title: "Einsatzzeiten und Schlüsselverwaltung",
        text: "Stimmen Sie die Reinigung mit Arbeitszeiten und Zutrittsregeln ab. Klären Sie Ansprechperson, Vertretung bei Ausfällen und den Umgang mit Rückmeldungen. Eine Begehung hilft, unterschiedliche Nutzungsintensitäten und schwer zugängliche Bereiche zu berücksichtigen.",
      },
    ],
    preparation: [
      "Bürofläche und Raumgruppen erfassen.",
      "Einsatzzeiten und Zutritt mit dem Betrieb abstimmen.",
      "Aufgaben an Arbeitsplätzen und Geräten abgrenzen.",
    ],
    costQuestion: "Was beeinflusst die Kosten der Büroreinigung?",
    costAnswer:
      "Fläche, Nutzung, Ausstattung und Häufigkeit beeinflussen den Aufwand. Vergleichen Sie Angebote mit identischem Reinigungsplan und klären Sie Verbrauchsartikel, Material, Zusatzarbeiten und Mehrwertsteuer. Halten Sie fest, wie Flächenänderungen abgerechnet werden.",
    faq: {
      question: "Ist Büroreinigung ausserhalb der Arbeitszeiten möglich?",
      answer:
        "Fragen Sie den Betrieb nach verfügbaren Zeitfenstern und vereinbaren Sie diese schriftlich. Zutritt, Schlüssel, Alarmierung und eine Kontaktperson für Rückfragen sollten vor dem ersten Einsatz geklärt sein.",
    },
  },
  {
    slug: "industrierueckbau",
    name: "Industrierückbau",
    family: "rueckbau",
    intro:
      "Für den Rückbau von Industrieanlagen vergleichen Sie spezialisierte Unternehmen nach Referenzen für ähnliche Hallen, Anlagen oder Produktionsbereiche. Beschreiben Sie den Bestand und die geplante Nutzung nach Abschluss der Arbeiten.",
    terms: [
      "industrierückbau",
      "industrierueckbau",
      "industrie rückbau",
      "industrieabbruch",
      "anlagendemontage",
    ],
    sections: [
      {
        title: "Anlagen und Gebäudeteile abgrenzen",
        text: "Erfassen Sie, welche Anlagen, Einrichtungen und baulichen Teile entfernt werden sollen. Vereinbaren Sie die Abgrenzung zu verbleibenden Bereichen und die nötigen Bestandsabklärungen. Vorhandene Pläne und eine gemeinsame Begehung helfen, den Umfang nachvollziehbar zu beschreiben.",
      },
      {
        title: "Rückbau mit dem Betrieb abstimmen",
        text: "Besprechen Sie Betriebszeiten, Etappen, Transportwege und Schutz angrenzender Bereiche. Lassen Sie Zuständigkeiten für technische Schnittstellen durch Fachleute klären. Wiederverwendung, Abtransport und Dokumentation der entfernten Materialien sollten Teil des Angebots sein.",
      },
    ],
    preparation: [
      "Anlagenübersicht, Pläne und Fotos bereitstellen.",
      "Betriebszeiten, Stillstandsfenster und Etappen benennen.",
      "Zu erhaltende Bereiche und Schnittstellen erfassen.",
    ],
    costQuestion: "Wie werden Kosten für Industrierückbau ermittelt?",
    costAnswer:
      "Die Kalkulation benötigt Angaben zu Anlagen, Konstruktion, Zugang und Ablauf. Vergleichen Sie Demontage, Baustelleneinrichtung, Schutzmassnahmen, Transport und Entsorgung getrennt. Lassen Sie erforderliche Abklärungen und zusätzliche Einsätze eindeutig zuordnen.",
    faq: {
      question: "Welche Referenzen sind für Industrierückbau hilfreich?",
      answer:
        "Fragen Sie nach Projekten mit ähnlichen Anlagen, Platzverhältnissen und betrieblichen Anforderungen. Klären Sie, welche Leistungen das Unternehmen selbst übernimmt und welche Fachbetriebe an Schnittstellen beteiligt werden.",
    },
  },
  {
    slug: "badrueckbau",
    name: "Badrückbau",
    family: "rueckbau",
    intro:
      "Für den Rückbau im Bad legen Sie vor der Offerte fest, welche Sanitärobjekte, Platten und Einbauten entfernt werden sollen. Stimmen Sie die Arbeiten mit der geplanten Badsanierung und den nachfolgenden Fachbetrieben ab.",
    terms: [
      "badrückbau",
      "badrueckbau",
      "bad rückbau",
      "bad rueckbau",
      "baddemontage",
      "badabbruch",
    ],
    sections: [
      {
        title: "Den Umfang im Badezimmer festlegen",
        text: "Beschreiben Sie Badewanne, Dusche, Möbel und weitere Einrichtungen. Halten Sie fest, ob Wandbeläge, Bodenaufbau und feste Einbauten zum Rückbau gehören. Vorhandene Fotos und Masse erleichtern die Vorbereitung einer Besichtigung.",
      },
      {
        title: "Schutz und Folgearbeiten koordinieren",
        text: "Klären Sie Zugang, Transportwege und den Schutz angrenzender Wohnräume. Bestehende Anschlüsse und die Übergabe an Sanitär- und Ausbauunternehmen sollten durch die zuständigen Fachbetriebe abgestimmt werden. Vereinbaren Sie, in welchem Zustand das Bad übergeben wird.",
      },
    ],
    preparation: [
      "Masse und Fotos des Badezimmers bereitstellen.",
      "Zu entfernende Beläge und Sanitärobjekte nennen.",
      "Übergabe an Sanitär- und Ausbauunternehmen abstimmen.",
    ],
    costQuestion: "Was beeinflusst die Kosten für einen Badrückbau?",
    costAnswer:
      "Grösse, Ausbauzustand und Umfang der zu entfernenden Teile bestimmen den Aufwand. Vergleichen Sie Schutzmassnahmen, Demontage, Abtransport, Entsorgung und Mehrwertsteuer. Klären Sie, welche Anschlussarbeiten separat beauftragt werden müssen.",
    faq: {
      question: "Gehört die neue Badeinrichtung zum Rückbauauftrag?",
      answer:
        "Halten Sie Rückbau und anschliessenden Ausbau als getrennte Leistungen fest. Vereinbaren Sie, wer die Koordination übernimmt und wann die nachfolgenden Fachbetriebe das Badezimmer übernehmen können.",
    },
  },
  {
    slug: "hausrueckbau",
    name: "Hausrückbau",
    family: "rueckbau",
    intro:
      "Für den Rückbau eines Hauses sollte das Unternehmen Gebäude und Grundstück vor Ort beurteilen. Vergleichen Sie Firmen anhand ähnlicher Objekte und eines klaren Umfangs für Gebäude, Keller, Fundamente und Nebengebäude.",
    terms: [
      "hausrückbau",
      "hausrueckbau",
      "haus rückbau",
      "haus rueckbau",
      "hausabbruch",
      "hausabriss",
      "gebäudeabbruch",
      "gebaeudeabbruch",
    ],
    sections: [
      {
        title: "Gebäude und Grundstück besichtigen",
        text: "Stellen Sie Pläne, Fotos und bekannte Angaben zur Bauweise bereit. Lassen Sie zu erhaltende Teile und die Grenzen des Auftrags erfassen. Klären Sie, ob Keller, Fundamente, Zufahrten und weitere Einrichtungen in den Hausrückbau einbezogen werden.",
      },
      {
        title: "Ablauf und Übergabezustand vereinbaren",
        text: "Besprechen Sie Baustelleneinrichtung, Transportwege und den Platzbedarf für Geräte und Container. Zuständigkeiten für notwendige Vorabklärungen und technische Schnittstellen sollten vor der Ausführung geklärt sein. Vereinbaren Sie den Zustand des Grundstücks bei Abschluss.",
      },
    ],
    preparation: [
      "Gebäudepläne, Baualter und Fotos bereitstellen.",
      "Keller, Fundamente und Nebengebäude erfassen.",
      "Zufahrt und gewünschte weitere Nutzung beschreiben.",
    ],
    costQuestion: "Was kostet der Rückbau eines Hauses?",
    costAnswer:
      "Gebäudegrösse, Konstruktion, Materialien und Zufahrt beeinflussen die Kosten. Lassen Sie Keller, Fundamente, Materialabklärungen und Entsorgung eindeutig aufführen. Vergleichen Sie nach einer Besichtigung den gleichen Übergabezustand und vereinbaren Sie den Umgang mit unerwarteten Funden.",
    faq: {
      question: "Sind Keller und Fundamente beim Hausrückbau enthalten?",
      answer:
        "Das ergibt sich aus dem vereinbarten Leistungsumfang. Lassen Sie ausdrücklich festhalten, was entfernt wird und was bestehen bleibt. Auch anschliessende Arbeiten am Grundstück sollten separat beschrieben werden.",
    },
  },
  {
    slug: "kellerrueckbau",
    name: "Kellerrückbau",
    family: "rueckbau",
    intro:
      "Beim Rückbau im Keller sind Umfang und Transportwege besonders wichtige Angaben für die Anfrage. Erfassen Sie feste Einbauten und Bauteile getrennt von Möbeln und anderen beweglichen Gegenständen, die nur geräumt werden sollen.",
    terms: [
      "kellerrückbau",
      "kellerrueckbau",
      "keller rückbau",
      "keller rueckbau",
      "kellerabbruch",
    ],
    sections: [
      {
        title: "Räumung und baulichen Rückbau erfassen",
        text: "Beschreiben Sie die betroffenen Kellerräume, Einbauten und Oberflächen. Vereinbaren Sie, was entfernt wird und welche Installationen bestehen bleiben. Lassen Sie notwendige Abklärungen für Eingriffe an festen Bauteilen durch Fachleute beurteilen.",
      },
      {
        title: "Zugang und Abtransport berücksichtigen",
        text: "Nennen Sie Türbreiten, Treppen und verfügbare Transportwege. Fotos helfen, enge Zugänge einzuordnen. Klären Sie den Schutz weiter genutzter Räume, gemeinsame Zugänge und die Bereitstellung von Flächen für Abtransport oder Container.",
      },
    ],
    preparation: [
      "Kellerräume und feste Einbauten dokumentieren.",
      "Treppen, Türen und Zugang mit Fotos beschreiben.",
      "Verbleibende Installationen und Nutzung nennen.",
    ],
    costQuestion: "Wovon hängen die Kosten eines Kellerrückbaus ab?",
    costAnswer:
      "Neben dem Umfang beeinflussen enge Transportwege und die zu entfernenden Materialien den Aufwand. Vergleichen Sie Schutzmassnahmen, Arbeitszeit, Abtransport und Entsorgung. Lassen Sie zusätzliche Räumungsarbeiten und notwendige Abklärungen gesondert beschreiben.",
    faq: {
      question: "Ist eine Kellerentrümpelung bereits ein Kellerrückbau?",
      answer:
        "Die Entfernung beweglicher Gegenstände ist als Räumung zu beschreiben. Wenn feste Einbauten oder Bauteile entfernt werden sollen, benötigen Sie einen entsprechend abgegrenzten Rückbauauftrag und passende Leistungsangaben des Betriebs.",
    },
  },
  {
    slug: "demontage",
    name: "Demontage",
    family: "rueckbau",
    intro:
      "Vergleichen Sie Unternehmen für Demontage und Rückbau anhand der zu entfernenden Einrichtungen. Ob Küche, Einbaumöbel oder technische Anlage: Beschreiben Sie, welche Teile erhalten, wiederverwendet oder entsorgt werden sollen.",
    terms: ["demontage", "demontier"],
    sections: [
      {
        title: "Einrichtungen und Wiederverwendung planen",
        text: "Erfassen Sie Masse, Material und Befestigung soweit bekannt und ergänzen Sie Fotos. Klären Sie, welche Teile nach der Demontage erhalten bleiben sollen. Verpackung, Zwischenlagerung und erneute Montage gehören nur bei ausdrücklicher Vereinbarung zum Auftrag.",
      },
      {
        title: "Schnittstellen und Abtransport abstimmen",
        text: "Lassen Sie bestehende Anschlüsse und technische Schnittstellen mit den zuständigen Fachbetrieben klären. Vereinbaren Sie Zugang und den Schutz angrenzender Oberflächen. Abtransport, Entsorgung und der Übergabezustand sollten im Leistungsverzeichnis stehen.",
      },
    ],
    preparation: [
      "Einrichtungen mit Massen und Fotos erfassen.",
      "Wiederverwendung oder Entsorgung je Teil festlegen.",
      "Zugang, Anschlüsse und Übergabe mit Fachbetrieben klären.",
    ],
    costQuestion: "Was beeinflusst die Kosten einer Demontage?",
    costAnswer:
      "Aufwand entsteht durch Grösse, Befestigung, Zugang und den gewünschten Erhaltungszustand. Vergleichen Sie Demontage, Schutz, Transport und Entsorgung getrennt. Klären Sie Zusatzkosten für Verpackung, Lagerung und weitere Montagearbeiten.",
    faq: {
      question: "Sind Abtransport und Entsorgung in der Demontage enthalten?",
      answer:
        "Das hängt vom Auftrag ab. Lassen Sie beides ausdrücklich aufführen und vereinbaren Sie, welche Teile beim Objekt bleiben. Wiederverwendbare Einrichtungen sollten mit gewünschtem Zustand und Übergabeort beschrieben werden.",
    },
  },
];

export const getRegionalService = (slug: string) => regionalServices.find((s) => s.slug === slug);
